/* ──────────────────────────────────────────────────────────────────────────
 * Window helper — bounding ticker / dividend / rate series to a preset
 * window before passing into runSimulation.
 *
 * Used by the simulator page when the user selects "1Y" / "5Y" / "10Y" /
 * "ALL". Pure function; no clock dependency (anchor is always the data's
 * latest date, not "today").
 *
 * **Use-what-we-have rule:** if the requested preset exceeds the data's
 * available range, return all available data. The preset is a request,
 * not a contract — a ticker with 7 years of history asked for "10Y" gets
 * 7 years. The scenario strip surfaces what was actually used.
 * ────────────────────────────────────────────────────────────────────────── */

export type WindowPreset = "1Y" | "5Y" | "10Y" | "ALL";

export const WINDOW_PRESETS: readonly WindowPreset[] = ["1Y", "5Y", "10Y", "ALL"] as const;

const YEARS_FOR_PRESET: Readonly<Record<Exclude<WindowPreset, "ALL">, number>> = {
  "1Y": 1,
  "5Y": 5,
  "10Y": 10,
};

/**
 * Slice a date-ordered series to the user's selected window. Anchors the
 * end at the data's latest date; start is `end - N years` or the data's
 * earliest date, whichever is later.
 *
 * Items must be ordered by `date` ascending (YYYY-MM-DD strings sort
 * lexicographically, which is the same as chronologically). Empty input
 * returns empty.
 */
export function windowDataForPreset<T extends { date: string }>(
  items: readonly T[],
  preset: WindowPreset,
): T[] {
  if (items.length === 0) return [];
  if (preset === "ALL") return [...items];

  const years = YEARS_FOR_PRESET[preset];
  const endDate = new Date(items[items.length - 1]!.date);
  const startDate = new Date(endDate);
  startDate.setFullYear(startDate.getFullYear() - years);
  const startDateStr = startDate.toISOString().slice(0, 10);

  return items.filter((i) => i.date >= startDateStr);
}

/**
 * Slice a date-ordered series to a user-supplied custom date range
 * `[startDate, endDate]` inclusive. Both endpoints are ISO YYYY-MM-DD.
 *
 * Use-what-we-have applies at both ends: if the requested range extends
 * past the data's bounds, the returned series is clamped silently.
 * Out-of-order or invalid ranges return an empty array.
 */
export function windowDataByRange<T extends { date: string }>(
  items: readonly T[],
  startDate: string,
  endDate: string,
): T[] {
  if (items.length === 0) return [];
  if (startDate > endDate) return [];
  return items.filter((i) => i.date >= startDate && i.date <= endDate);
}
